import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  addMonths,
  format,
  getDaysInMonth,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useTranslation } from '@/hooks/useTranslation';

export interface DatePickerProps {
  /** ISO date string ("YYYY-MM-DD") or empty string. */
  value: string;
  onChange: (iso: string) => void;
  placeholder?: string;
  /** Minimum selectable date (ISO). Defaults to 130 years ago. */
  min?: string;
  /** Maximum selectable date (ISO). Defaults to today. */
  max?: string;
  disabled?: boolean;
  hasError?: boolean;
  /** When true the picker opens above the trigger if there's not enough room below. */
  autoFlip?: boolean;
  className?: string;
  id?: string;
  ariaLabel?: string;
}

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];
const WEEKDAYS_EN_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_AR_SHORT = ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت'];

const toIso = (d: Date): string => format(d, 'yyyy-MM-dd');

const parseSafe = (iso: string | undefined | null): Date | null => {
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
};

const clamp = (d: Date, min: Date, max: Date): Date => {
  if (isBefore(d, min)) return min;
  if (isAfter(d, max)) return max;
  return d;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder,
  min,
  max,
  disabled,
  hasError,
  autoFlip = true,
  className,
  id,
  ariaLabel,
}) => {
  const { isRTL } = useTranslation();
  const months = isRTL ? MONTHS_AR : MONTHS_EN;
  const weekdays = isRTL ? WEEKDAYS_AR_SHORT : WEEKDAYS_EN_SHORT;

  const today = startOfDay(new Date());
  const maxDate = useMemo(
    () => parseSafe(max) ?? today,
    [max], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const minDate = useMemo(() => {
    const p = parseSafe(min);
    if (p) return p;
    const d = new Date(today);
    d.setFullYear(d.getFullYear() - 130);
    return d;
  }, [min]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = parseSafe(value);
  const initialView = selected ?? clamp(today, minDate, maxDate);

  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(startOfMonth(initialView));
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Re-anchor view when popover opens, so it lands on the selected month.
  useEffect(() => {
    if (open) {
      const target = parseSafe(value) ?? clamp(today, minDate, maxDate);
      setViewMonth(startOfMonth(target));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click / Esc. The popover lives in a portal (outside
  // containerRef), so clicks inside it must be excused explicitly or it
  // would close on its own first click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Position the popover in viewport (fixed) coordinates anchored to the
  // trigger. Rendering it in a portal + fixed positioning is what keeps it
  // from being clipped by an ancestor's `overflow` (the Add Availability
  // modal wraps the form in overflow-hidden + overflow-y-auto, which used
  // to crop the calendar and bleed it past the modal edge). Recomputed on
  // open + scroll + resize so it tracks the trigger while open.
  useEffect(() => {
    if (!open) return;
    const recompute = () => {
      const t = triggerRef.current;
      if (!t) return;
      const rect = t.getBoundingClientRect();
      const margin = 8;
      const gap = 6;
      const desiredH = 360;
      const width = Math.min(320, window.innerWidth - margin * 2);

      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;

      // Prefer opening downward; flip up only when down can't fit the full
      // calendar AND up has more room. Whichever side we pick, cap the height
      // to the room actually available there so the popover scrolls
      // internally instead of spilling its footer off-screen — the modal it
      // lives in is `fixed`, so the page can't scroll to reveal a clipped
      // bottom.
      const openDown = !autoFlip || spaceBelow >= desiredH || spaceBelow >= spaceAbove;

      let top: number;
      let maxHeight: number;
      if (openDown) {
        top = rect.bottom + gap;
        maxHeight = window.innerHeight - top - margin;
      } else {
        maxHeight = Math.min(desiredH, spaceAbove);
        top = rect.top - gap - maxHeight;
      }
      maxHeight = Math.max(160, Math.min(maxHeight, desiredH));
      top = Math.max(margin, top);

      // Align to the trigger's inline-start, clamped to the viewport.
      let left = isRTL ? rect.right - width : rect.left;
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

      setCoords({ top, left, width, maxHeight });
    };
    recompute();
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open, autoFlip, isRTL]);

  const handlePick = (d: Date) => {
    if (isBefore(d, minDate) || isAfter(d, maxDate)) return;
    onChange(toIso(d));
    setOpen(false);
  };

  const buildDays = (): Array<Date | null> => {
    const first = startOfMonth(viewMonth);
    const total = getDaysInMonth(viewMonth);
    const startOffset = first.getDay(); // 0=Sun
    const cells: Array<Date | null> = Array.from({ length: startOffset }, () => null);
    for (let day = 1; day <= total; day++) {
      cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    // Always 6 rows for stable height.
    while (cells.length < 42) cells.push(null);
    return cells;
  };

  const years: number[] = useMemo(() => {
    const list: number[] = [];
    for (let y = maxDate.getFullYear(); y >= minDate.getFullYear(); y--) list.push(y);
    return list;
  }, [minDate, maxDate]);

  const canPrev = !isBefore(addMonths(viewMonth, -1), startOfMonth(minDate));
  const canNext = !isAfter(addMonths(viewMonth, 1), startOfMonth(maxDate));

  const Prev = isRTL ? ChevronRight : ChevronLeft;
  const Next = isRTL ? ChevronLeft : ChevronRight;

  const triggerLabel = selected
    ? format(selected, 'MMM dd, yyyy')
    : placeholder ?? (isRTL ? 'اختر تاريخاً' : 'Select date');

  const days = buildDays();

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
        className="w-full inline-flex items-center gap-2 px-3 min-h-[44px] rounded-lg border text-sm focus:outline-none focus:ring-2 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          borderColor: hasError ? 'var(--color-error)' : 'var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-primary)',
          color: selected ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
        }}
      >
        <CalendarIcon
          className="w-4 h-4 flex-shrink-0"
          style={{ color: 'var(--color-text-tertiary)' }}
        />
        <span className="flex-1 text-start truncate">{triggerLabel}</span>
      </button>

      {open && coords && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={ariaLabel ?? (isRTL ? 'منتقي التاريخ' : 'Date picker')}
          className="fixed z-[60] p-3 rounded-xl border"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border-primary)',
            boxShadow: 'var(--shadow-xl)',
            top: coords.top,
            left: coords.left,
            width: coords.width,
            maxHeight: coords.maxHeight,
            overflowY: 'auto',
          }}
        >
          {/* Header: prev — month/year selectors — next */}
          <div className="flex items-center gap-1.5 mb-2">
            <button
              type="button"
              disabled={!canPrev}
              onClick={() => setViewMonth((m) => addMonths(m, -1))}
              className="p-1.5 rounded-md disabled:opacity-40"
              style={{ color: 'var(--color-text-secondary)' }}
              aria-label={isRTL ? 'الشهر السابق' : 'Previous month'}
            >
              <Prev className="w-4 h-4" />
            </button>

            <select
              value={viewMonth.getMonth()}
              onChange={(e) => {
                const next = new Date(viewMonth);
                next.setMonth(Number(e.target.value));
                setViewMonth(clamp(next, startOfMonth(minDate), startOfMonth(maxDate)));
              }}
              className="flex-1 min-h-[34px] px-2 rounded-md border text-sm focus:outline-none focus:ring-2"
              style={{
                borderColor: 'var(--color-border-primary)',
                backgroundColor: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
              }}
            >
              {months.map((m, i) => (
                <option key={m} value={i}>{m}</option>
              ))}
            </select>

            <select
              value={viewMonth.getFullYear()}
              onChange={(e) => {
                const next = new Date(viewMonth);
                next.setFullYear(Number(e.target.value));
                setViewMonth(clamp(next, startOfMonth(minDate), startOfMonth(maxDate)));
              }}
              className="min-h-[34px] px-2 rounded-md border text-sm focus:outline-none focus:ring-2"
              style={{
                borderColor: 'var(--color-border-primary)',
                backgroundColor: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
              }}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <button
              type="button"
              disabled={!canNext}
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="p-1.5 rounded-md disabled:opacity-40"
              style={{ color: 'var(--color-text-secondary)' }}
              aria-label={isRTL ? 'الشهر التالي' : 'Next month'}
            >
              <Next className="w-4 h-4" />
            </button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 mb-1">
            {weekdays.map((w) => (
              <div
                key={w}
                className="text-center text-[10px] font-semibold uppercase tracking-wider py-1"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                {w}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((d, i) => {
              if (!d) return <div key={`e-${i}`} className="h-9" />;
              const disabledDay = isBefore(d, minDate) || isAfter(d, maxDate);
              const isSelected = selected && isSameDay(d, selected);
              const isToday = isSameDay(d, today);
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  disabled={disabledDay}
                  onClick={() => handlePick(d)}
                  className="h-9 rounded-md text-sm font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: isSelected
                      ? 'var(--color-primary-600)'
                      : isToday
                        ? 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)'
                        : 'transparent',
                    color: isSelected
                      ? 'white'
                      : isToday
                        ? 'var(--color-primary-700)'
                        : 'var(--color-text-primary)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !disabledDay) {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isSelected) return;
                    e.currentTarget.style.backgroundColor = isToday
                      ? 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)'
                      : 'transparent';
                  }}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between mt-2 pt-2 border-t"
            style={{ borderColor: 'var(--color-border-primary)' }}
          >
            <button
              type="button"
              onClick={() => {
                if (isAfter(today, maxDate) || isBefore(today, minDate)) return;
                onChange(toIso(today));
                setOpen(false);
              }}
              className="text-xs font-semibold"
              style={{ color: 'var(--color-primary-600)' }}
            >
              {isRTL ? 'اليوم' : 'Today'}
            </button>
            {selected && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                className="text-xs"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                {isRTL ? 'مسح' : 'Clear'}
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default DatePicker;
